import { ReactNode, useEffect, useMemo, useState } from 'react';
import { UseFormReturn } from 'react-hook-form';

import { Label } from 'components/Label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from 'components/Select';
import { RHFDebounceSelect } from 'components/input/RHFDebounceSelect';
import { Grid } from 'components/layout';
import { Typography } from 'components/typography';
import {
  getActiveLtlIntegration,
  isLtlCustomerSupportedTMS,
} from 'hooks/useCustomers';
import { useServiceFeatures } from 'hooks/useServiceContext';
import {
  LTLCustomerStaff,
  getLTLCustomerStaff,
} from 'lib/api/getLTLCustomerStaff';
import { Maybe, Undef } from 'types/UtilityTypes';
import { IntegrationType, TMS } from 'types/enums/Integrations';
import { mapCustomerToSelectOptions } from 'utils/loadInfoAndBuilding';

import { useQuickQuoteContext } from '../context/QuickQuoteContext';
import { QuickQuoteInputs } from '../types';

interface QuoteInformationSectionProps {
  formMethods: UseFormReturn<QuickQuoteInputs, any, QuickQuoteInputs>;
  showLTLStaff?: boolean;
  afterCustomer?: ReactNode;
  customerRequired?: boolean;
  // Dense panels (Batch LTL) label the pickers without a section heading.
  showHeading?: boolean;
  // Staff to pick automatically when the list loads (e.g. the rep's earlier choice).
  preferredStaffOwnerId?: number;
}

type LTLCustomerRecord = {
  externalTMSID?: string;
  name?: string;
  externalId?: string;
  ID?: number;
  id?: number;
  enableAutoFreightClass?: boolean;
};

const getCustomerDbId = (customer?: LTLCustomerRecord) =>
  customer?.ID ?? customer?.id;

export const QuoteInformationSection = ({
  formMethods,
  showLTLStaff = true,
  afterCustomer,
  customerRequired = false,
  showHeading = true,
  preferredStaffOwnerId,
}: QuoteInformationSectionProps) => {
  const {
    control,
    formState: { errors },
    watch,
  } = formMethods;

  const { ltlTMSIntegrations } = useServiceFeatures();

  // Customers from context are already filtered for LTL.
  const {
    customers,
    customersLoading,
    handleResetCustomerSearch,
    handleCustomerSearch,
    ltlCustomer,
    setLtlCustomer,
    setLtlCustomerStaff,
  } = useQuickQuoteContext();

  const customerNameValue = watch('customerName');

  useEffect(() => {
    if (!customerNameValue) {
      setLtlCustomer(null);
      setLtlCustomerStaff(null);
      return;
    }

    const customer = customers?.find(
      (c) => c.externalTMSID === customerNameValue
    );

    if (customer) {
      const dbId = getCustomerDbId(customer as LTLCustomerRecord);
      setLtlCustomer({
        customerId: customer.externalTMSID,
        customerName: customer.name,
        externalId: (customer as LTLCustomerRecord).externalId,
        customerDbId: dbId,
        enableAutoFreightClass: (customer as LTLCustomerRecord)
          .enableAutoFreightClass,
      });
      return;
    }

    // Search/reset can replace customers without the selected row; keep a prior resolve.
    if (ltlCustomer?.customerId === customerNameValue) {
      return;
    }

    setLtlCustomer(null);
    setLtlCustomerStaff(null);
  }, [
    customerNameValue,
    customers,
    ltlCustomer?.customerId,
    setLtlCustomer,
    setLtlCustomerStaff,
  ]);

  return (
    <Grid gap={showHeading ? 'lg' : 'sm'} className='w-full mx-0'>
      {showHeading && (
        <Typography
          variant='h5'
          className='text-neutral-800 font-semibold whitespace-nowrap'
        >
          Quote Information
        </Typography>
      )}

      {ltlTMSIntegrations &&
        ltlTMSIntegrations.length > 0 &&
        isLtlCustomerSupportedTMS(ltlTMSIntegrations) && (
          <div>
            <RHFDebounceSelect
              name='customerName'
              label='Customer'
              control={control}
              errors={errors}
              data={customers}
              isLoading={customersLoading}
              showSearchParamDropdown={false}
              resetOptionsHandler={handleResetCustomerSearch}
              fetchOptions={handleCustomerSearch}
              mapOptions={mapCustomerToSelectOptions}
              required={Boolean(customerRequired)}
              parentOnChange={(_, selectedRawItem) => {
                const c = selectedRawItem as Undef<LTLCustomerRecord>;
                if (c?.externalTMSID) {
                  const dbId = getCustomerDbId(c);
                  setLtlCustomer({
                    customerId: c.externalTMSID,
                    customerName: c.name ?? c.externalTMSID,
                    externalId: c.externalId,
                    customerDbId: dbId,
                    enableAutoFreightClass: c.enableAutoFreightClass,
                  });
                }
              }}
              callFieldOnChangeWithParent
            />
          </div>
        )}

      {afterCustomer}

      {showLTLStaff && (
        <LTLCustomerStaffSection
          preferredStaffOwnerId={preferredStaffOwnerId}
        />
      )}
    </Grid>
  );
};

const LTLCustomerStaffSection = ({
  preferredStaffOwnerId,
}: {
  preferredStaffOwnerId?: number;
}) => {
  const [isLTLCustomerStaffSearchLoading, setIsLTLCustomerStaffSearchLoading] =
    useState(false);
  const [ltlCustomerStaffList, setLtlCustomerStaffList] =
    useState<Maybe<LTLCustomerStaff[]>>(null);
  const [staffSearchError, setStaffSearchError] = useState(false);
  const [selectedStaffValue, setSelectedStaffValue] =
    useState<Maybe<{ label: string; value: number; data: LTLCustomerStaff }>>(
      null
    );

  const { ltlCustomer, setLtlCustomerStaff } = useQuickQuoteContext();
  const { ltlTMSIntegrations, quotingIntegrations } = useServiceFeatures();
  const activeLtlIntegration = useMemo(
    () => getActiveLtlIntegration(ltlTMSIntegrations),
    [ltlTMSIntegrations]
  );
  const activeIntegrationId = activeLtlIntegration?.id;
  const activeIntegrationName = activeLtlIntegration?.name;

  useEffect(() => {
    if (!ltlCustomer) {
      setLtlCustomerStaffList(null);
      setSelectedStaffValue(null);
      setLtlCustomerStaff(null);
      setStaffSearchError(false);
      return;
    }

    setSelectedStaffValue(null);
    setLtlCustomerStaff(null);
    setLtlCustomerStaffList(null);
    setStaffSearchError(false);

    let cancelled = false;
    setIsLTLCustomerStaffSearchLoading(true);
    const pricingIntegration = quotingIntegrations?.find((integration) => {
      return (
        integration.type === IntegrationType.Quoting &&
        integration.name === activeIntegrationName
      );
    });

    getLTLCustomerStaff(ltlCustomer.customerName, ltlCustomer.customerId, {
      tmsIntegrationId: activeIntegrationId,
      pricingIntegrationId: pricingIntegration?.id,
      integrationName: activeIntegrationName,
    }).then((result) => {
      if (cancelled) {
        return;
      }

      setIsLTLCustomerStaffSearchLoading(false);

      if (result.isOk()) {
        setLtlCustomerStaffList(result.value.customerList);
      } else {
        setLtlCustomerStaffList(null);
        setStaffSearchError(true);
      }
    });
    return () => {
      cancelled = true;
    };
  }, [
    ltlCustomer,
    activeIntegrationId,
    activeIntegrationName,
    quotingIntegrations,
    setLtlCustomerStaff,
  ]);

  useEffect(() => {
    if (selectedStaffValue) {
      setLtlCustomerStaff({
        email: selectedStaffValue.data.email,
        ownerId: selectedStaffValue.data.ownerId,
        ownerName: selectedStaffValue.data.ownerName,
        tmsIntegrationId: selectedStaffValue.data.tmsIntegrationId,
      });
    } else {
      setLtlCustomerStaff(null);
    }
  }, [selectedStaffValue, setLtlCustomerStaff]);

  useEffect(() => {
    if (activeIntegrationName !== TMS.Tai) {
      return;
    }

    if (isLTLCustomerStaffSearchLoading) {
      return;
    }

    if (selectedStaffValue || !ltlCustomerStaffList?.length) {
      return;
    }

    const staff =
      ltlCustomerStaffList.find(
        ({ ownerId }) => ownerId === preferredStaffOwnerId
      ) ?? ltlCustomerStaffList[0];
    setSelectedStaffValue({
      label: staff.ownerName,
      value: staff.ownerId,
      data: staff,
    });
  }, [
    activeIntegrationName,
    isLTLCustomerStaffSearchLoading,
    ltlCustomerStaffList,
    selectedStaffValue,
    preferredStaffOwnerId,
  ]);

  let staffPlaceholder = 'Select staff';
  if (isLTLCustomerStaffSearchLoading) {
    staffPlaceholder = 'Loading...';
  } else if (staffSearchError) {
    staffPlaceholder = 'Unable to load staff';
  }

  if (!ltlCustomer) {
    return null;
  }

  return (
    <div className='w-full'>
      <Label name='staff' className='mb-0'>
        Customer Staff
      </Label>
      <Select
        onValueChange={(value) => {
          const staff = ltlCustomerStaffList?.find(
            (s) => s.ownerId.toString() === value
          );
          if (staff) {
            setSelectedStaffValue({
              label: staff.ownerName,
              value: staff.ownerId,
              data: staff,
            });
          } else {
            setSelectedStaffValue(null);
          }
        }}
        value={selectedStaffValue?.value?.toString()}
        disabled={isLTLCustomerStaffSearchLoading || staffSearchError}
      >
        <SelectTrigger className='w-full mt-1'>
          <SelectValue placeholder={staffPlaceholder} />
        </SelectTrigger>
        <SelectContent>
          {ltlCustomerStaffList?.map((staff) => (
            <SelectItem key={staff.ownerId} value={staff.ownerId.toString()}>
              {staff.ownerName}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
};
